import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Stack,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import {
  duplicarTorneio,
  DuplicarTorneioInput,
} from '../../services/torneioService';
import { duplicarTorneioSchema } from '../../schemas/duplicarTorneioSchemas';

interface DuplicarTorneioDialogProps {
  open: boolean;
  onClose: () => void;
  torneioId: string;
  torneioNome: string;
  torneioUsername: string;
}

const DuplicarTorneioDialog: React.FC<DuplicarTorneioDialogProps> = ({
  open,
  onClose,
  torneioId,
  torneioNome,
  torneioUsername,
}) => {
  const [nome, setNome] = useState('');
  const [username, setUsername] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [dataInscricaoInicio, setDataInscricaoInicio] = useState('');
  const [dataInscricaoFim, setDataInscricaoFim] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    nome?: string;
    username?: string;
    dataInicio?: string;
    dataFim?: string;
    dataInscricaoInicio?: string;
    dataInscricaoFim?: string;
  }>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setNome(torneioNome + ' - cópia');
      setUsername(torneioUsername + '2');
      setDataInicio('');
      setDataFim('');
      setDataInscricaoInicio('');
      setDataInscricaoFim('');
      setErrors({});
    }
  }, [open, torneioNome, torneioUsername]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const validate = () => {
    const result = duplicarTorneioSchema.safeParse({
      nome: nome.trim(),
      username: username.trim(),
      dataInicio,
      dataFim,
      dataInscricaoInicio,
      dataInscricaoFim,
    });
    if (!result.success) {
      const errs: typeof errors = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof typeof errors;
        errs[field] = err.message;
      });
      setErrors(errs);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const input: DuplicarTorneioInput = {
        nome: nome.trim(),
        username: username.trim(),
        dataInicio,
        dataFim,
        dataInscricaoInicio,
        dataInscricaoFim,
      };
      const result = await duplicarTorneio(torneioId, input);
      setSnackbar({
        open: true,
        message: 'Torneio duplicado com sucesso',
        severity: 'success',
      });
      onClose();
      navigate(`/liga/tournament/${result.id}`);
    } catch (err: any) {
      const msg = err?.message || 'Erro ao duplicar torneio.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="duplicar-torneio-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="duplicar-torneio-title">Duplicar Torneio</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Um novo torneio será criado copiando os dados do torneio
              &quot;{torneioNome}&quot;. Categorias, organizadores, patrocinadores
              e descontos serão copiados automaticamente.
            </Typography>
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                <strong>Corrija os seguintes erros:</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                  {errors.nome && <li>{errors.nome}</li>}
                  {errors.username && <li>{errors.username}</li>}
                  {errors.dataInicio && <li>{errors.dataInicio}</li>}
                  {errors.dataFim && <li>{errors.dataFim}</li>}
                  {errors.dataInscricaoInicio && <li>{errors.dataInscricaoInicio}</li>}
                  {errors.dataInscricaoFim && <li>{errors.dataInscricaoFim}</li>}
                </ul>
              </Alert>
            )}
            <TextField
              label="Nome do torneio"
              required
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              error={Boolean(errors.nome)}
              helperText={errors.nome}
            />
            <TextField
              label="Username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={Boolean(errors.username)}
              helperText={errors.username}
            />
            <div>
              <label className="d-block mb-2" htmlFor="duplicar-data-inicio">
                Data de início
              </label>
              <DatePicker
                id="duplicar-data-inicio"
                selected={dataInicio ? new Date(dataInicio) : null}
                onChange={(date: Date | null) =>
                  setDataInicio(date ? date.toISOString().split('T')[0] : '')
                }
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="Selecione a data de início"
                popperProps={{ strategy: 'fixed' }}
              />
              {errors.dataInicio && (
                <div className="text-danger small">{errors.dataInicio}</div>
              )}
            </div>
            <div>
              <label className="d-block mb-2" htmlFor="duplicar-data-fim">
                Data de fim
              </label>
              <DatePicker
                id="duplicar-data-fim"
                selected={dataFim ? new Date(dataFim) : null}
                onChange={(date: Date | null) =>
                  setDataFim(date ? date.toISOString().split('T')[0] : '')
                }
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="Selecione a data de fim"
                popperProps={{ strategy: 'fixed' }}
              />
              {errors.dataFim && (
                <div className="text-danger small">{errors.dataFim}</div>
              )}
            </div>
            <div>
              <label className="d-block mb-2" htmlFor="duplicar-inscricao-inicio">
                Data de início de inscrição
              </label>
              <DatePicker
                id="duplicar-inscricao-inicio"
                selected={dataInscricaoInicio ? new Date(dataInscricaoInicio) : null}
                onChange={(date: Date | null) =>
                  setDataInscricaoInicio(date ? formatDateTimeLocal(date) : '')
                }
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={30}
                dateFormat="dd/MM/yyyy HH:mm"
                className="form-control"
                placeholderText="Selecione data e hora"
                popperProps={{ strategy: 'fixed' }}
              />
              {errors.dataInscricaoInicio && (
                <div className="text-danger small">{errors.dataInscricaoInicio}</div>
              )}
            </div>
            <div>
              <label className="d-block mb-2" htmlFor="duplicar-inscricao-fim">
                Data de fim de inscrição
              </label>
              <DatePicker
                id="duplicar-inscricao-fim"
                selected={dataInscricaoFim ? new Date(dataInscricaoFim) : null}
                onChange={(date: Date | null) =>
                  setDataInscricaoFim(date ? formatDateTimeLocal(date) : '')
                }
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={30}
                dateFormat="dd/MM/yyyy HH:mm"
                className="form-control"
                placeholderText="Selecione data e hora"
                popperProps={{ strategy: 'fixed' }}
              />
              {errors.dataInscricaoFim && (
                <div className="text-danger small">{errors.dataInscricaoFim}</div>
              )}
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Duplicando...' : 'Duplicar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DuplicarTorneioDialog;
